import type { IPublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import AuthGuard from '../auth/auth.guard';
import AuthService from '../auth/auth.service';
import { MsalNavigationClient } from '../auth/msal-navigation-client';
import PublicGuard from '../auth/public.guard';
import useApiInterceptors from '../hooks/useApiInterceptors';
import useBrowserButtonEffect from '../hooks/useBrowserButtonEffect';
import useCustomerServiceHook from '../hooks/useCustomerServiceHook';
import useDeviceBreakpoint from '../hooks/useDeviceBreakpoint';
import useInitialLoaderHook from '../hooks/useInitialLoaderHook';
import useOfferCreation from '../hooks/useOfferCreation';
import useThemeHook from '../hooks/useThemeHook';
import useUserInfo from '../hooks/useUserInfo';
import { RootState } from '../redux/types';
import { isAdb2cEnabled } from '../utils/feature-flags';
import { isAdmin } from '../utils/helper';
import Loader from '../view/b2b/components/loader/loader';
import Modal from '../view/b2b/components/modal/modal';
import ActiveAppsOverview from '../view/common/active-lease-applications/overview/overview';
import AGB from '../view/common/agb/agb';
import CreditCheckLoader from '../view/common/animation/credit-check-loader/credit-check-loader';
import ForgetPassword from '../view/common/auth-pages/forget-password/forget-password';
import LoginPage from '../view/common/auth-pages/login-page/login-page';
import RegisterPage from '../view/common/auth-pages/register-page/register-page';
import ResetPassword from '../view/common/auth-pages/reset-password/reset-password';
import Bootstrap from '../view/common/bootstrap/bootstrap';
import ContractsHistory from '../view/common/contracts-history/contracts-history';
import DatenschutzKontodaten from '../view/common/datenschutz-kontodaten/datenschutz-kontodaten';
import { LeaseApplicationError } from '../view/common/error-boundary/error';
import ErrorBoundary from '../view/common/error-boundary/error-boundary';
import FeedbackSurvey from '../view/common/feedback/feedback-survey';
import Snacker from '../view/common/feedback/snacker';
import Footer from '../view/common/footer/footer';
import Header from '../view/common/header/header';
import Home from '../view/common/home/home';
import IDF from '../view/common/idf/idf';
import IdnowLoggedOut from '../view/common/idnow-response/idnow-loggedout/idnow-loggedout';
import Impressum from '../view/common/impressum/impressum';
import LeaseApplication from '../view/common/lease-application/lease-application';
import Logout from '../view/common/logout/logout';
import Notification from '../view/common/notification/notification';
import SummaryB2B from '../view/common/offer/summary-b2b';
import SummaryB2C from '../view/common/offer/summary-b2c';
import Profile from '../view/common/profile/profile';
import Verified from '../view/common/verified/verified';
import './app.css';

interface FrameProps {
  children: React.ReactNode;
}

const Frame: React.FC<FrameProps> = ({ children }) => (
  <>
    <Header />
    <main id='main'>{children}</main>
    <Footer />
    <FeedbackSurvey />
    <Snacker />
  </>
);

interface AppProps {
  msalInstance: IPublicClientApplication;
}

const AppContent: React.FC = () => {
  // Hooks
  useDeviceBreakpoint();
  useBrowserButtonEffect();
  useUserInfo();
  useThemeHook();
  useCustomerServiceHook();
  const leaseApplicationError = useOfferCreation();
  useApiInterceptors();

  // If there was an error during offer creation or offer loading,
  // we throw an error to be caught by the ErrorBoundary and show the fallback UI
  if (leaseApplicationError) {
    throw new LeaseApplicationError('Error during offer creation');
  }

  // Initial Animation Loader
  const { loadingAnimationVisibility } = useInitialLoaderHook();

  // Cache Store
  const {
    loaderModalVisible,
    creditCheckModalVisibility,
    creditCheckModalAnimation,
  } = useSelector((state: RootState) => state.cache);

  return (
    <AuthService>
      <Routes>
        <Route
          path='/*'
          element={
            <Frame>
              <Navigate to='/start' replace />
            </Frame>
          }
        />

        {isAdmin() && (
          <>
            <Route
              path='/home'
              element={
                <Frame>
                  <AuthGuard Component={<Home />} />
                </Frame>
              }
            />
            <Route
              path='/idf'
              element={
                <Frame>
                  <AuthGuard Component={<IDF />} />
                </Frame>
              }
            />
          </>
        )}

        <Route
          path='/logout'
          element={
            <Frame>
              <AuthGuard Component={<Logout />} />
            </Frame>
          }
        />

        <Route
          path='/contracts-history'
          element={
            <Frame>
              <AuthGuard Component={<ContractsHistory />} />
            </Frame>
          }
        />

        <Route
          path='/datenschutz-kontodaten'
          element={
            <Frame>
              <DatenschutzKontodaten />
            </Frame>
          }
        />

        <Route
          path='/impressum'
          element={
            <Frame>
              <Impressum />
            </Frame>
          }
        />

        <Route
          path='/reset-password'
          element={
            <Frame>
              <PublicGuard Component={<ResetPassword />} />
            </Frame>
          }
        />

        <Route
          path='/change-password/:token'
          element={
            <Frame>
              <PublicGuard Component={<ForgetPassword />} />
            </Frame>
          }
        />

        <Route
          path='/verified'
          element={
            <Frame>
              <Verified />
            </Frame>
          }
        />

        <Route
          path='/agb'
          element={
            <Frame>
              <AGB />
            </Frame>
          }
        />

        <Route
          path='/login'
          element={
            <PublicGuard
              Component={
                <Frame>
                  <LoginPage />
                </Frame>
              }
            />
          }
        />

        <Route
          path='/register/:offerId?'
          element={
            isAdb2cEnabled() ? (
              <PublicGuard
                Component={
                  <Frame>
                    <RegisterPage />
                  </Frame>
                }
              />
            ) : (
              <Navigate to='/login' replace />
            )
          }
        />

        <Route
          path='/offer'
          element={
            <Frame>
              <SummaryB2B />
            </Frame>
          }
        />

        <Route
          path='/b2cOffer'
          element={
            <Frame>
              <SummaryB2C />
            </Frame>
          }
        />

        <Route
          path='/idnow/response/:status'
          element={
            <Frame>
              <IdnowLoggedOut />
            </Frame>
          }
        />

        <Route
          path='/active-applications/overview'
          element={<AuthGuard Component={<ActiveAppsOverview />} />}
        />

        <Route
          path='/profile'
          element={
            <Frame>
              <AuthGuard Component={<Profile />} />
            </Frame>
          }
        />

        <Route
          path={`/lease-application/:id/*`}
          element={
            <Frame>
              <AuthGuard Component={<LeaseApplication />} />
            </Frame>
          }
        />

        <Route
          path='/start/:publicationId?/:pricingId?/:offerId?'
          element={
            <Frame>
              <PublicGuard Component={<Bootstrap />} />
            </Frame>
          }
        />
      </Routes>
      <Notification />

      {(loaderModalVisible || loadingAnimationVisibility) && (
        <div
          className='fullscreen-loading'
          role='alertdialog'
          aria-busy='true'
          aria-live='polite'
        >
          <div className='fullscreen-loading-card'>
            <h2 className='fullscreen-loading-title'>Wird geladen...</h2>
            <span className='fullscreen-loading-spinner' aria-hidden='true' />
            <p className='fullscreen-loading-text'>
              Bitte warten Sie, während wir Ihre Inhalte abrufen
            </p>
          </div>
        </div>
      )}

      <Modal
        isOpen={creditCheckModalVisibility}
        type='middle'
        closeButtonVisibility={false}
        direction='none'
        background='default'
        hasInAnimated
      >
        <div
          className={`loader-container ${
            creditCheckModalAnimation === 'In' ? 'credit' : 'credit-out'
          } `}
        >
          <CreditCheckLoader />
          <h2 className='title'>Bonitätsprüfung gestartet</h2>
          <Loader />
        </div>
      </Modal>
    </AuthService>
  );
};

const App: React.FC<AppProps> = ({ msalInstance }) => {
  const navigate = useNavigate();

  useEffect(() => {
    msalInstance.setNavigationClient(new MsalNavigationClient(navigate));
  }, [msalInstance, navigate]);

  return (
    <ErrorBoundary>
      <MsalProvider instance={msalInstance}>
        <AppContent />
      </MsalProvider>
    </ErrorBoundary>
  );
};

export default App;
